import React from "react";
import { Bookmark } from "lucide-react";
import { resourceListService } from "../../services/resourceListService";

/**
 * AddToList component for authenticated users.
 * Shows "Add to a List" (single) or "Save all results on this page" (bulk) button
 * that opens a modal with options to create a new list or add to an existing one.
 *
 * Props:
 *   result      - single search result (detail page mode)
 *   results     - array of results (bulk/header mode)
 *   pageNum     - current page number (for bulk mode label)
 */
function AddToList({ result, results, pageNum, compact = false }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [lists, setLists] = React.useState([]);
  const [listsLoading, setListsLoading] = React.useState(true);
  const [selectedListId, setSelectedListId] = React.useState("");
  const [newListName, setNewListName] = React.useState("");
  const [createError, setCreateError] = React.useState("");
  const [addNotice, setAddNotice] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");

  const isBulk = !result && Array.isArray(results);
  const resourceName = isBulk
    ? `${results.length} result${results.length === 1 ? "" : "s"} from page ${pageNum || 1}`
    : (result?.name || result?.agency_name || "");
  const resourceId = isBulk ? "Multi" : (result?.program_site_id || result?.id || "");

  const itemIds = isBulk
    ? results.map((r) => r.program_site_id || r.id)
    : [result?.program_site_id || result?.id];

  React.useEffect(() => {
    if (modalOpen && listsLoading) {
      resourceListService.getAll().then((data) => {
        setLists(Array.isArray(data) ? data : []);
        setListsLoading(false);
      }).catch(() => setListsLoading(false));
    }
  }, [modalOpen]);

  React.useEffect(() => {
    if (lists.length > 0 && !selectedListId) {
      setSelectedListId(lists[0].hashid);
    }
  }, [lists]);

  function handleOpen(e) {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  }

  function handleClose() {
    setModalOpen(false);
    setAddNotice("");
    setCreateError("");
  }

  function handleCreateList(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!newListName.trim()) return;

    resourceListService.create(newListName.trim()).then((newList) => {
      setLists([newList, ...lists]);
      addToList(newList.hashid, newListName.trim());
      setNewListName("");
    }).catch((err) => {
      setCreateError(err?.error_message || "Failed to create list.");
    });
  }

  function handleAddToExistingList() {
    if (!selectedListId) {
      setAddNotice("Please make a valid selection.");
      return;
    }
    const listName = lists.find((l) => l.hashid === selectedListId)?.name || "";
    addToList(selectedListId, listName);
  }

  function addToList(hashid, listName) {
    resourceListService
      .addItem(hashid, itemIds)
      .then((data) => {
        setModalOpen(false);
        const counts = data?.counts || { added: itemIds.length, present: 0 };
        showConfirmation(listName, counts);
      })
      .catch((err) => {
        if (err?.status === 404) {
          setAddNotice("Please make a valid selection.");
        } else {
          setAddNotice("Already on that list.");
        }
      });
  }

  function showConfirmation(listName, counts) {
    let msg;
    if (counts.added === 1 && counts.present === 0) {
      msg = `Added to ${listName}.`;
    } else if (counts.present === 0) {
      msg = `${counts.added} resources added to ${listName}.`;
    } else if (counts.added === 0 && counts.present === 1) {
      msg = `Already on ${listName}.`;
    } else if (counts.added === 0) {
      msg = `${counts.present} resources already on ${listName}.`;
    } else {
      msg = `${counts.added} resources added to ${listName}. ${counts.present} resources already on the list.`;
    }
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 6000);
  }

  const resourceListsPath = window.UW211?.APP_PATHS?.resourceLists || "/my211/resource-lists";

  return (
    <div className="share-result">
      <div
        className="open-create-resource-list-form small-label result-action pointer"
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        aria-label={compact ? "Add to a List" : undefined}
        title={compact ? "Add to a List" : undefined}
      >
        {result ? (
          compact ? (
            <>
              <Bookmark aria-hidden="true" size={17} strokeWidth={1.8} />
              <span className="visually-hidden">Add to a List</span>
            </>
          ) : (
            <>
              <i className="ico ico-list2" />
              {" "}Add to a List
            </>
          )
        ) : (
          <div className="small-label save-resource-container">
            Add Results to List
          </div>
        )}
        <span className="added-success-confirmation">
          {successMessage && (
            <span className="popover bottom" style={{ display: "block", position: "absolute", zIndex: 1060 }}>
              <div className="popover-content">{successMessage}</div>
            </span>
          )}
        </span>
      </div>

      {modalOpen && (
        <div
          className="modal add-to-list-modal"
          role="dialog"
          tabIndex={-1}
          style={{ display: "block", zIndex: 9999 }}
        >
          <div className="modal-dialog" role="document">
            <div className="share-content">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="sub-headliner">Add to a Resource List</h4>
                  <h6>{resourceName}</h6>
                </div>

                <div className="modal-body">
                  <form className="create-container" onSubmit={handleCreateList}>
                    <div className="input-error">{createError}</div>
                    <input
                      className="input"
                      id={`list_name_${resourceId}`}
                      name="list_name"
                      placeholder="New Resource List Name"
                      value={newListName}
                      onChange={(e) => { setNewListName(e.target.value); setCreateError(""); }}
                    />
                    <p className="note">Note: The list name is displayed when shared. Avoid sensitive information.</p>
                    <input className="submit-list btn btn-primary" type="submit" value="Create List" />
                  </form>

                  {listsLoading ? (
                    <div className="select-wrap loading-state">Lists are loading...</div>
                  ) : (
                    <div className="select-region">
                      <span className="select-wrap">
                        <div className="lists-dropdown-region">
                          <select
                            className="lists-dropdown"
                            id="resource-lists"
                            name="resource-lists"
                            value={selectedListId}
                            onChange={(e) => setSelectedListId(e.target.value)}
                          >
                            {lists.map((list) => (
                              <option key={list.hashid} value={list.hashid}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </span>
                      <div className="add">{addNotice}</div>
                      <button
                        className="add-resource-to-list btn btn-primary"
                        name="Add"
                        onClick={handleAddToExistingList}
                      >
                        Add to List
                      </button>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <div className="resource-list-link">
                    <a href={resourceListsPath}>My Resource Lists</a>
                  </div>
                  <div className="dismiss-link">
                    <a className="dismiss" href="#" onClick={(e) => { e.preventDefault(); handleClose(); }}>
                      Dismiss
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddToList;
